import Header from "@/components/header/header";
// eslint-disable-next-line max-len
import { useTemplatesAndInstructionsDesktop } from "./templatesAndInstructionsDesktop/useTemplatesAndInstructionsDesktop";
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@fluentui/react-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Skeleton from "react-loading-skeleton";
import { Template } from "@/api/openapi";
import { translate } from "@/config/translator";
import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import FilePreviewDialog from "@/components/filePreviewDialog/filePreviewDialog";
import PageSwitcher from "@/components/pageSwitcher/PageSwitcher";
import { Dispatch, SetStateAction } from "react";

type TemplatesAndInstructionsDesktopProps = {
  templates: Template[] | undefined;
  onDownloadTemplate: (templateId: string) => void;
  setPage: Dispatch<SetStateAction<number>>;
  page: number;
  totalPages: number | undefined;
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
};

export function TemplatesAndInstructionsDesktop({
  templates,
  onDownloadTemplate,
  page,
  setPage,
  totalPages,
  searchQuery,
  setSearchQuery,
}: TemplatesAndInstructionsDesktopProps) {
  const {
    lang,
    columns,
    setTemplateActionOpenId,
    templateActionOpenId,
    openFilePreview,
    onOpenPreview,
    templatePreviewOpenId,
    onClosePreview,
  } = useTemplatesAndInstructionsDesktop();

  return (
    <div className="text-font-black p-10 gap-8 flex flex-col">
      <Header
        text={"templatesAndInstructions"}
        searchValue={searchQuery}
        onChangeSearch={setSearchQuery}
      />
      <div className="overflow-x-scroll w-[calc(100vw-21rem)] desktop:w-full">
        <Table>
          <colgroup>
            {columns.map(({ className }) => (
              <col className={className} />
            ))}
          </colgroup>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHeaderCell key={`users-table-header-${column.key}`}>
                  {column.label}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>

          {/* TABLE BODY */}
          <TableBody>
            {(templates ?? Array.from({ length: 20 })).map(
              (template: Template | undefined, index) => (
                <TableRow
                  className={
                    index % 2 === 0 ? "bg-white" : "bg-canvas-secondary"
                  }
                  key={`uploads-webapp-table-row-${template?.id ?? index}`}
                >
                  {/* TITLE */}
                  <TableCell>{template?.title ?? <Skeleton />}</TableCell>

                  {/* TYPE */}
                  <TableCell>
                    {template ? (
                      translate(template.category ?? "generic", lang)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>

                  {/* ACTION */}
                  <TableCell>
                    <Popover
                      onOpenChange={(_, data) => {
                        if (!template) {
                          return;
                        }
                        if (data.open) {
                          setTemplateActionOpenId(template.id);
                        } else {
                          setTemplateActionOpenId(null);
                        }
                      }}
                      open={template && templateActionOpenId === template.id}
                    >
                      <PopoverTrigger disableButtonEnhancement>
                        <FontAwesomeIcon
                          icon={faEllipsis}
                          className="text-font-secondary text-3xl cursor-pointer"
                        />
                      </PopoverTrigger>

                      <PopoverSurface>
                        <div className="flex flex-col gap-2 w-50">
                          <h3 className="font-bold text-lg">
                            {translate("action", lang)}
                          </h3>
                          <Button
                            appearance="primary"
                            disabled={!template}
                            onClick={() =>
                              template ? onDownloadTemplate(template?.id) : {}
                            }
                          >
                            {translate("download", lang)}
                          </Button>
                          <Button
                            onClick={() => (template ? onOpenPreview() : {})}
                          >
                            {translate("preview", lang)}
                          </Button>
                        </div>
                      </PopoverSurface>
                    </Popover>
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </div>
      <FilePreviewDialog
        file={openFilePreview}
        isOpen={!!openFilePreview}
        onClose={() => onClosePreview()}
        onDownload={() =>
          templatePreviewOpenId ? onDownloadTemplate(templatePreviewOpenId) : {}
        }
      />
      <div className="flex flex-row justify-end pt-10">
        <PageSwitcher
          currentPage={page}
          setCurrentPage={setPage}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
}
